/**
 * Kaartsymbolen en legenda, gedeeld door de projectkaart en Vooronderzoek, zodat beide kaarten
 * hetzelfde laten zien:
 *
 *   definitieve boring   gevuld donkerblauw rondje
 *   definitieve peilbuis gevuld blauw rondje (een boring met een peilbuis erin)
 *   geplande boring      ring in de kleur van de soort (boring oranje, peilbuis blauw, ...)
 *   gepland, uitgevoerd  groen met vinkje
 */
import { html } from 'lit';
import L from 'leaflet';
import { PLANNED_KIND_COLORS } from './kaart-import/leaflet-layers.js';

export const DEFINITIEF_KLEUR = { boring: '#1f3a5f', peilbuis: PLANNED_KIND_COLORS.peilbuis };

/** Heeft deze (kaart)boring een peilbuis? Zelfde veld als in de projectkaart. */
export const heeftPeilbuis = (b) => !!(b?.peilbuisName || b?.hasPeilbuis);

/** Icoon van een definitieve boring. Geselecteerd = groter en groen. */
export function boringIcon(b, { selected = false } = {}) {
  const size = selected ? 16 : 12;
  const color = selected ? 'var(--mv-primary, #2e9e5b)' : (heeftPeilbuis(b) ? DEFINITIEF_KLEUR.peilbuis : DEFINITIEF_KLEUR.boring);
  return L.divIcon({
    className: 'mv-marker',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px ${selected ? 5 : 4}px rgba(0,0,0,.45)"></div>`,
    iconSize: [size, size], iconAnchor: [size / 2, size / 2],
  });
}

const NAAM = { peilbuis: 'peilbuis', proefgat: 'proefgat', sleuf: 'sleuf', overig: 'overig' };

/** Is de legenda ingeklapt? Eén keuze voor beide kaarten, per browser onthouden. */
export function legendaIngeklapt() {
  try { return localStorage.getItem('mv-kaart-legenda') === 'dicht'; } catch (_) { return false; }
}

export function bewaarLegendaIngeklapt(dicht) {
  try { localStorage.setItem('mv-kaart-legenda', dicht ? 'dicht' : 'open'); } catch (_) { /* privévenster */ }
}

/**
 * De legenda; alleen de regels die in dit project voorkomen. Inklapbaar via de titel:
 * `ingeklapt` en `onToggle` komen van de component (die hem opnieuw tekent).
 */
export function renderKaartLegenda({ borings = [], plannedPoints = [], ingeklapt = false, onToggle = null } = {}) {
  const pts = plannedPoints || [];
  if (!(borings || []).length && !pts.length) return '';
  const metPeilbuis = (borings || []).some(heeftPeilbuis);
  const zonder = (borings || []).some((b) => !heeftPeilbuis(b));
  const soorten = [...new Set(pts.map((p) => p.kind || 'boring'))].filter((k) => k !== 'boring');
  const kop = html`<button class="lg-title" aria-expanded=${ingeklapt ? 'false' : 'true'} @click=${() => onToggle?.()}
      title=${ingeklapt ? 'Legenda tonen' : 'Legenda inklappen'}>Legenda <span class="lg-pijl">${ingeklapt ? '▸' : '▾'}</span></button>`;
  if (ingeklapt) return html`<div class="legend dicht" aria-label="Legenda">${kop}</div>`;
  return html`<div class="legend" aria-label="Legenda">
    ${kop}
    ${zonder || !metPeilbuis ? html`<div class="lg-row"><span class="lg-def"></span>Definitieve boring <span class="lg-sub">· in het veld vastgelegd</span></div>` : ''}
    ${metPeilbuis ? html`<div class="lg-row"><span class="lg-def" style="background:${DEFINITIEF_KLEUR.peilbuis}"></span>Definitieve peilbuis</div>` : ''}
    ${pts.length ? html`
      <div class="lg-row"><span class="lg-plan" style="border-color:${PLANNED_KIND_COLORS.boring}"></span>Geplande boring <span class="lg-sub">· nog te boren</span></div>
      ${soorten.map((k) => html`
        <div class="lg-row"><span class="lg-plan ${k === 'proefgat' || k === 'sleuf' ? 'square' : ''}" style="border-color:${PLANNED_KIND_COLORS[k] || PLANNED_KIND_COLORS.overig}"></span>Gepland: ${NAAM[k] || k}</div>`)}
      <div class="lg-row"><span class="lg-done">✓</span>Gepland en uitgevoerd</div>` : ''}
  </div>`;
}

/** Stijlen van de legenda (positie zet de component zelf). Geen accenten: staat in een css-sjabloon. */
export const KAART_LEGENDA_CSS = `
  .legend { position:absolute; left:10px; z-index:500; background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:8px;
            padding:7px 10px; box-shadow:0 1px 4px rgba(0,0,0,0.12); font-size:0.72rem; color:var(--mv-text); display:grid; gap:4px; }
  .lg-title { font-size:0.62rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--mv-text-muted);
              border:none; background:none; padding:0; font-family:inherit; cursor:pointer; text-align:left; display:flex; align-items:center; gap:4px; }
  .lg-title:hover { color:var(--mv-text); }
  .lg-pijl { font-size:0.7rem; }
  .legend.dicht { padding:5px 10px; }
  .lg-row { display:flex; align-items:center; gap:7px; white-space:nowrap; }
  .lg-sub { color:var(--mv-text-muted); }
  .lg-def { width:12px; height:12px; border-radius:50%; background:#1f3a5f; border:2px solid #fff; box-shadow:0 1px 3px rgba(0,0,0,.45); box-sizing:border-box; flex-shrink:0; }
  .lg-plan { width:12px; height:12px; border-radius:50%; border:2.5px solid; background:rgba(255,255,255,.85); box-sizing:border-box; flex-shrink:0; }
  .lg-plan.square { border-radius:3px; }
  .lg-done { width:12px; height:12px; border-radius:50%; background:#2e9e5b; color:#fff; font:700 8px/12px system-ui; text-align:center; flex-shrink:0; }
`;
